<template>
	<AppCardPage>
		<InventarisationCustomerMenu
			v-model="selectedCustomerId"
			:customers="data.customers"
			:max-entries="2"
			@add="addCustomer"
			@remove="removeCustomer"
		/>

		<slot
			:selected-customer-id
			:selected-index
			:customers="data.customers"
		></slot>
	</AppCardPage>
</template>

<script setup lang="ts">
import {
	newCustomer,
	removeCustomerCategoryEntries,
} from "@/helpers/inventarisation/customers";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { useInventarisationStore } from "@/store/useInventarisationStore";
import { computed, nextTick, watch } from "vue";
import { storeToRefs } from "pinia";
import { toast } from "vue-sonner";

const dossierDataStore = useDossierDataStore();
const { data } = storeToRefs(dossierDataStore);
const inventarisationStore = useInventarisationStore();
const { selectedCustomerId } = storeToRefs(inventarisationStore);

const selectedIndex = computed(() => {
	return data.value.customers.findIndex(
		(c) => c.id === selectedCustomerId.value,
	);
});

// Auto-select first customer if none is selected and customers exist
watch(
	() =>
		[
			data.value.customers.length,
			data.value.customers.map((c) => c.id).join(","),
			selectedCustomerId.value,
		] as const,
	([customersLength, customerIds, currentSelectedId]) => {
		if (!currentSelectedId && customersLength > 0) {
			selectedCustomerId.value = data.value.customers[0]?.id;
		}
		// If selected customer no longer exists, select first available
		else if (
			currentSelectedId &&
			!customerIds.includes(currentSelectedId) &&
			customersLength > 0
		) {
			selectedCustomerId.value = data.value.customers[0]?.id;
		}
	},
	{ immediate: true },
);

/**
 * Adds a new customer to the inventarisation data.
 */
function addCustomer() {
	const customer = newCustomer();
	data.value.customers.push(customer);
	selectedCustomerId.value = customer.id;
}

/**
 * Removes customer with the specified id from inventarisation data.
 */
function removeCustomer(id: string) {
	const index = data.value.customers.findIndex(
		(customer) => customer.id === id,
	);

	if (index === -1) {
		return toast.error(`Could not remove customer: id "${id}" not found`);
	}

	data.value.customers.splice(index, 1);

	// Clean up category data (income/assets/liabilities) tied to the removed
	// customer so no ghost records linger and skew later calculations.
	removeCustomerCategoryEntries(
		{
			income: data.value.income,
			assets: data.value.assets,
			liabilities: data.value.liabilities,
		},
		id,
	);

	if (data.value.customers.length !== 0) {
		const newIndex = index > 0 ? index - 1 : 0;
		const newId = data.value.customers[newIndex]?.id;
		void nextTick(() => (selectedCustomerId.value = newId));
	}
}
</script>
